import { motion, useAnimation } from 'framer-motion'
import React, {
  forwardRef, useEffect, useRef, useState,
} from 'react'
import styled from 'styled-components'

import { Room } from './Room'
import { Expo } from '../../../../types/viewModels'
import {
  createRoomMovement,
  HoverText,
  RoomHoverText,
  RoomMovement,
  RoomNavigator,
  Scroller,
  Space,
  StillScroller,
} from '../../../RoomExperience'
import CloseIcon from '../../../Svg/CloseIcon'
import { mediaMax, numberSize as breakpoints } from '../../../../constants/breakpoints'
import config from '../../../../../config'

interface RoomDesktopProps {
  room: Expo
  contentLoaded?: boolean
  hrefRoomPrevious?: string
  hrefRoomNext?: string
  color?: string
  mobileView: boolean
}
const RoomDesktop = forwardRef<HTMLDivElement, RoomDesktopProps>(
  ({
    room, 
    contentLoaded, 
    hrefRoomPrevious, 
    hrefRoomNext, 
    color,
    mobileView,
  }, ref) => {
    const [hoverText, setHoverText] = useState<HoverText>({ title: '', description: '' })
    const handleHover = (title = '', description = '') => {
      setHoverText({ title, description })
    }
    const handleMouseLeave = () => {
      setHoverText({ title: '', description: '' })
    }

    const scrollerRef = useRef<HTMLDivElement>(null)
    const roomMovementRef = useRef<RoomMovement>()
    const controls = useAnimation()
    useEffect(() => {
      if (!scrollerRef.current) {
        return
      }

      roomMovementRef.current = createRoomMovement(scrollerRef.current)

      if (contentLoaded) {
        controls
          .start({
            translateZ: [0, 500],
            transition: {
              duration: 0.4,
              delay: 0.7,
              ease: 'easeInOut',
            },
          })
          .then(() => {
            roomMovementRef.current && roomMovementRef.current.start()
          })
      }

      return () => {
        roomMovementRef.current && roomMovementRef.current.stop()
      }
    }, [controls, contentLoaded])

    const [roomIsUp, setRoomIsUp] = useState(false)
    const [videoSrc, setVideoSrc] = useState('')
    
    // const [isMobile, setIsMobile] = useState(false)

    // useEffect(() => {
    //   if (window.matchMedia(`(max-width: ${breakpoints.mobileL}px)`).matches) {
    //     setIsMobile(true)
    //   } else {
    //     setIsMobile(false)
    //   }
    // }, [isMobile])

    const toggleUpDown = () => {
      roomMovementRef.current && roomMovementRef.current.stop()

      if (!roomIsUp) {
        controls.start({
          translateZ: [500, 0],
          translateY: [0, 1000],
          rotateX: [0, -20],
          transition: {
            duration: 1,
            ease: 'easeInOut',
          },
        })
        setRoomIsUp(true)
      }
      else {
        controls.start({
          translateZ: [0, 500],
          translateY: [1000, 0],
          rotateX: [-20, 0],
          transition: {
            duration: 1,
            ease: 'easeInOut',
          },
        })
        setRoomIsUp(false)
      }
    }

    const controlsVideoWindow = useAnimation()

    const showVideo = (src: string) => {
      setVideoSrc(src)
      toggleUpDown()
      controlsVideoWindow.start({
        zIndex: 200,
        transition: {
          delay: 0.8,
          duration: 1,
          ease: 'easeInOut',
        },
      })
    }

    const hideVideo = () => {
      setVideoSrc('')
      toggleUpDown()
      controlsVideoWindow.start({
        zIndex: -1,
        transition: {
          delay: 0.2,
          duration: 1,
          ease: 'easeInOut',
        },
      })
    }

    const roomComponent = <Room
      className="active"
      room={room}
      windowOnMouseEnter={handleHover}
      windowOnMouseLeave={handleMouseLeave}
      showVideo={showVideo}
      mobileView={mobileView}
      color={color}
    />

    return (
      <>
        <Space ref={ref}>
          {
            // window.innerWidth < 550
            // isMobile
            mobileView
              ? (
                <StillScroller ref={scrollerRef}>
                  { roomComponent }
                </StillScroller>
              ) : (
                <Scroller animate={controls} ref={scrollerRef}>
                  { roomComponent }
                </Scroller>
              )
          }
          <RoomHoverText title={hoverText.title} description={hoverText.description} />
        </Space>
        <VideoContainer animate={controlsVideoWindow}>
          {
            videoSrc && videoSrc !== '' && (
              <>
                <iframe title="video" src={videoSrc} width="1280" height="675" />
                <CloseButton onClick={() => hideVideo()}>
                  <CloseIcon height="100%" width="100%" />
                </CloseButton>
              </>
            )
          }
        </VideoContainer>
        {!mobileView && <RoomNavigator hrefBack={hrefRoomPrevious} hrefForward={hrefRoomNext} hrefDoor={'/showrooms'} />}

        {mobileView && room.logo && room.logoLink && (
          <CompanyLogoLink href={room.logoLink} target="_blank">
            <CompanyLogo src={`${config.LISA_IMAGE_BASE}${room.sponsorpagelogo[0].assetId}&action=width:500`} alt={`company logo - ${room.company}`} />
          </CompanyLogoLink>
        )}
        {mobileView && room.logo && !room.logoLink
          && (
            <CompanyLogoWithoutLink
              src={`${config.LISA_IMAGE_BASE}${room.sponsorpagelogo[0].assetId}&action=width:500`}
              alt={`company logo - ${room.company}`}
            />
          )}
      </>
    )
  },
)

export default RoomDesktop

const CompanyLogo = styled.img`
  height: 100%;
  img {
    object-fit: contain !important;
  }
`

const CompanyLogoLink = styled.a`
  display:block;
  position: absolute !important;
  left: 20%;
  width: 40%;
  max-height: 100%;
  height:80%;
  transform: translate3d(0, 0, 5px);
  cursor:pointer;
  pointer-events:all;

  @media ${mediaMax.mobileL} {
    position: fixed !important;
    left: 4%;
    bottom: 2%;
    width: 25vw;
    height: 25vw;

    img {
      object-fit: contain;
      width: 100%;
    }
  }
`

const CompanyLogoWithoutLink = styled.img`
  height: auto;
  width: 40%;
  left: 20%;
  max-height: 100%;
  height:80%;
  position: absolute !important;
  transform: translate3d(0, 0, 5px);
  img {
    object-fit: contain !important;
  }

  @media ${mediaMax.mobileL} {
    position: fixed !important;
    left: 4%;
    bottom: 4%;
    width: 25vw;
    height: 40px;
  }
`

const VideoContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: -1;
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding-top: max(100px, 7vh);
  padding-left: max(30px, 3 * 3.125vw);
  padding-right: max(30px, 3 * 3.125vw);

  @media ${mediaMax.mobileL} {
    padding-top: 0;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    
    & iframe {
      height: 50vh;
      width: 100vw;
      object-fit: contain;
    }
  }
`

const CloseButton = styled.button`
  height: 3.125vw;
  min-height: 45px;
  width: 3.125vw;
  min-width: 45px;
  background-color: white;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  top: min(120px, 2 * 3.125vw);
  right: 30px;
  margin-left: 10px;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 25%);
  border: none;

  @media ${mediaMax.mobileL} {
    position: absolute;
    top: 17vh;
    right: 4vw;
  }
`
